import { MainContainer } from '@/components';
import { useAuth } from '@/components/auth/authProvider';
import RequirePermissions from '@/components/auth/requirePermissions';
import { isPalmettoFinanceUser } from '@/components/auth/RequirePricingAdminUser';
import { BatchView } from '@/components/Payouts/BatchView';
import { useGetOrganizationDetailsQuery } from '@/services/organizations';
import { Box, SelectInputNative } from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  generatePath,
  matchPath,
  NavLink,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import { ProgramType, ProgramTypeLabels, UserPermissions } from 'types';

import PageHeader from '../../PageHeader';
import { ProjectTransactionsView } from '../ProjectTransactionsView';

import { AllTransactions } from './AllTransactions';
import { ImportTransactionsCsv } from './ImportTransactionsCsv';
import { InvoiceNeeded } from './InvoiceNeeded';
import { Paid } from './Paid';
import { PausedTransactions } from './PausedTransactions';
import { PayeeTransactions } from './PayeeTransactions';
import { PaymentPlan } from './PaymentPlan';
import { PaymentPlans } from './PaymentPlans';
import { PendingPaymentsPlan } from './PendingPaymentPlans';
import { Queued } from './Queued';
import { UnbatchedTransactions } from './UnbatchedTransactions';

const baseRoute = '/admin/payouts/';
const routes = {
  paymentPlans: ':programType/payment-plans',
  paymentPlan: ':programType/payment-plans/:id',
  queued: 'queued',
  approved: 'approved',
  pausedTransactions: 'paused-transactions',
  unbatchedTransactions: 'unbatched-transactions',
  invoiceNeeded: 'invoice-needed',
  groupBatches: 'group-batches/:batchDate',
  batch: 'batches/:batchId',
  allTransactions: 'all-transactions',
  organizationTransactions: 'organization-transactions',
  pendingPaymentPlanChange: ':programType/pending-payment-plan-change',
};

const routesWithProgramType = Object.entries(routes).filter(([_key, value]) => value.includes(':programType'));

const programTypesOptions = Object.entries(ProgramTypeLabels).map(([key, value]) => ({
  label: value,
  value: key,
}));

export function Payouts() {
  const params = useParams();
  const { claims } = useAuth();
  const { data: organizationData } = useGetOrganizationDetailsQuery(
    {
      alias: claims?.org,
    },
    { skip: !claims?.org },
  );
  const isPalmettoFinance = isPalmettoFinanceUser(claims);
  const isLightReachAccountingAdmin =
    isPalmettoFinance && claims?.permissions?.includes(UserPermissions.lightReachAccountingAdmin);
  const [programType, setProgramType] = useState<ProgramType>(
    (params?.programType as ProgramType) ?? programTypesOptions[0].value,
  );
  const isLightReachAccountingReader = claims?.permissions?.includes(UserPermissions.lightReachAccountingReader);
  const isLightReachAccountingSettings = claims?.permissions?.includes(UserPermissions.lightReachAccountingSettings);
  const { bulkTransactionCsvUpload = false } = useFlags();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (organizationData?.programTypes?.length) {
      const orgProgramType = organizationData.programTypes[0];
      if (programType !== orgProgramType) setProgramType(orgProgramType);
    }
  }, [organizationData]);

  const showProgramTypeSelection = useMemo(() => {
    return (
      !location ||
      routesWithProgramType.some(([_key, value]) => matchPath(`${baseRoute}${value}`, location.pathname))
    );
  }, [location]);

  const handleProgramTypeChange = (event: any) => {
    const selectedProgramType: ProgramType = event.target.value;
    setProgramType(selectedProgramType);
    const pathPattern =
      Object.values(routes).find((pattern) => matchPath(`${baseRoute}${pattern}`, location.pathname)) || '';
    const path = generatePath(`${baseRoute}/${pathPattern}`, {
      ...params,
      programType: selectedProgramType,
    });
    navigate(path);
  };

  return (
    <>
      <Helmet>
        <title>Payouts</title>
      </Helmet>
      <MainContainer maxWidth="100">
        <RequirePermissions
          permissions={[
            UserPermissions.orgAccountingAdmin,
            UserPermissions.lightReachAccountingAdmin,
            UserPermissions.lightReachAccountingReader,
          ]}
          checkAllPermissions={false}
        >
          {showProgramTypeSelection && (
            <Box maxWidth="2xl">
              <SelectInputNative
                id="required"
                label="Program Type"
                value={programType}
                onChange={handleProgramTypeChange}
                options={programTypesOptions}
                menuPortalTarget={document.body}
              />
            </Box>
          )}
        </RequirePermissions>
        <PageHeader title={`Payments`}>
          {isLightReachAccountingAdmin && bulkTransactionCsvUpload && <ImportTransactionsCsv />}
        </PageHeader>

        <Box direction="row" borderWidth="0 0 xs 0" borderColor="separator">
          <RequirePermissions
            permissions={[
              UserPermissions.lightReachAccountingAdmin,
              UserPermissions.orgAccountingAdmin,
              UserPermissions.lightReachAccountingReader,
            ]}
            checkAllPermissions={false}
          >
            <NavLink to="queued" style={{ textDecoration: 'none', fontWeight: '500' }}>
              {({ isActive }) => (
                <Box as="span" padding="md" className={isActive ? 'tab-active' : ''} hover={{ color: 'primary' }}>
                  Queued
                </Box>
              )}
            </NavLink>
          </RequirePermissions>
          <RequirePermissions
            permissions={[
              UserPermissions.lightReachAccountingAdmin,
              UserPermissions.orgAccountingAdmin,
              UserPermissions.lightReachAccountingReader,
            ]}
            checkAllPermissions={false}
          >
            <NavLink to="approved" style={{ textDecoration: 'none', fontWeight: '500' }}>
              {({ isActive }) => (
                <Box as="span" padding="md" className={isActive ? 'tab-active' : ''} hover={{ color: 'primary' }}>
                  Approved
                </Box>
              )}
            </NavLink>
          </RequirePermissions>
          <RequirePermissions
            permissions={[
              UserPermissions.lightReachAccountingAdmin,
              UserPermissions.orgAccountingAdmin,
              UserPermissions.lightReachAccountingReader,
            ]}
            checkAllPermissions={false}
          >
            <NavLink to={routes.allTransactions} style={{ textDecoration: 'none', fontWeight: '500' }}>
              {({ isActive }) => (
                <Box as="span" padding="md" className={isActive ? 'tab-active' : ''} hover={{ color: 'primary' }}>
                  All Transactions
                </Box>
              )}
            </NavLink>
          </RequirePermissions>
          <RequirePermissions
            permissions={[
              UserPermissions.lightReachAccountingAdmin,
              UserPermissions.lightReachAccountingReader,
              UserPermissions.orgAccountingAdmin,
            ]}
            checkAllPermissions={false}
          >
            <NavLink to={`paused-transactions`} style={{ textDecoration: 'none', fontWeight: '500' }}>
              {({ isActive }) => (
                <Box as="span" padding="md" className={isActive ? 'tab-active' : ''} hover={{ color: 'primary' }}>
                  Paused Transactions
                </Box>
              )}
            </NavLink>
          </RequirePermissions>
          <RequirePermissions
            permissions={[UserPermissions.lightReachAccountingAdmin, UserPermissions.lightReachAccountingReader]}
            checkAllPermissions={false}
          >
            <NavLink to={routes.unbatchedTransactions} style={{ textDecoration: 'none', fontWeight: '500' }}>
              {({ isActive }) => (
                <Box as="span" padding="md" className={isActive ? 'tab-active' : ''} hover={{ color: 'primary' }}>
                  Unbatched Transactions
                </Box>
              )}
            </NavLink>
          </RequirePermissions>
          {isLightReachAccountingAdmin && (
            <NavLink to={routes.organizationTransactions} style={{ textDecoration: 'none', fontWeight: '500' }}>
              {({ isActive }) => (
                <Box as="span" padding="md" className={isActive ? 'tab-active' : ''} hover={{ color: 'primary' }}>
                  Organization Transactions
                </Box>
              )}
            </NavLink>
          )}
          <RequirePermissions
            permissions={[UserPermissions.lightReachAccountingSettings]}
            checkAllPermissions={true}
          >
            <NavLink
              to={routes.pendingPaymentPlanChange.replace(':programType', programType)}
              style={{ textDecoration: 'none', fontWeight: '500' }}
            >
              {({ isActive }) => (
                <Box as="span" padding="md" className={isActive ? 'tab-active' : ''} hover={{ color: 'primary' }}>
                  Pending Payment Plan Changes
                </Box>
              )}
            </NavLink>
          </RequirePermissions>
          <RequirePermissions
            permissions={[
              UserPermissions.lightReachAccountingAdmin,
              UserPermissions.orgAccountingAdmin,
              UserPermissions.lightReachAccountingReader,
            ]}
            checkAllPermissions={false}
          >
            <NavLink to="invoice-needed" style={{ textDecoration: 'none', fontWeight: '500' }}>
              {({ isActive }) => (
                <Box as="span" padding="md" className={isActive ? 'tab-active' : ''} hover={{ color: 'primary' }}>
                  Invoice Needed
                </Box>
              )}
            </NavLink>
          </RequirePermissions>
          <RequirePermissions
            permissions={[
              UserPermissions.lightReachAccountingAdmin,
              UserPermissions.lightReachAccountingReader,
              UserPermissions.lightReachAccountingSettings,
            ]}
            checkAllPermissions={false}
          >
            <NavLink to={`${programType}/payment-plans`} style={{ textDecoration: 'none', fontWeight: '500' }}>
              {({ isActive }) => (
                <Box as="span" padding="md" className={isActive ? 'tab-active' : ''} hover={{ color: 'primary' }}>
                  Payment Plans
                </Box>
              )}
            </NavLink>
          </RequirePermissions>
        </Box>
        <Routes>
          <Route path={routes.queued} element={<Queued />} />
          <Route path={routes.approved} element={<Paid />} />
          <Route path={routes.invoiceNeeded} element={<InvoiceNeeded />} />
          <Route path={routes.batch} element={<BatchView />} />
          <Route path={routes.groupBatches} element={<ProjectTransactionsView />} />
          <Route path={routes.allTransactions} element={<AllTransactions />} />
          <Route path={routes.pausedTransactions} element={<PausedTransactions />} />
          {isLightReachAccountingSettings && (
            <Route
              path={routes.pendingPaymentPlanChange}
              element={<PendingPaymentsPlan organization={organizationData as any} programType={programType} />}
            />
          )}

          {(isLightReachAccountingAdmin || isLightReachAccountingReader || isLightReachAccountingSettings) && (
            <>
              <Route path={routes.paymentPlans} element={<PaymentPlans programType={programType} />} />
              <Route path={routes.paymentPlan} element={<PaymentPlan />} />
              <Route path={routes.unbatchedTransactions} element={<UnbatchedTransactions />} />
            </>
          )}
          {isLightReachAccountingAdmin && (
            <Route path={routes.organizationTransactions} element={<PayeeTransactions />} />
          )}
        </Routes>
      </MainContainer>
    </>
  );
}
